/**
 * 代理商 realm（`/api/admin/reseller/*`）——批93-C §2.2。
 *
 * ★★ **獨立一個前綴、獨立一支 sub-router**，而不是在既有端點裡加「if 代理商就過濾」。
 *   這樣「代理商能不能打這支」變成**結構規則**（路徑開頭 `/api/admin/reseller/` 或
 *   `GET /me`，其餘一律 403），後端有 `test_reseller_gate.py` 機械驗證；既有 63 支
 *   console 端點零改動也就零風險。前端同理：代理商的頁面只 import 這個檔，
 *   不會不小心呼到 `ls.ts` 裡那些會 403 的東西。
 *
 * 三件代理商**做不到**的事（後端沒有端點，不是前端藏起來）：開通／撤銷／刪除授權、
 * 看別家客戶、看主機細節（`hostname`／`host_info` 不在投影裡）。
 */
import { api } from './client'
import type {
  CustomerPatchBody,
  CustomerProfile,
  CustomerProfileOptions,
  LicenseBrief,
  LicenseReq,
  ProductSpec,
  RedeemCampaign,
  RedeemCode,
  RedeemCodePage,
  RedemptionPage,
} from './ls'

const P = '/api/admin/reseller'

// --- 產品範圖 -------------------------------------------------------------

/**
 * 這家代理商賣得了的產品（含憑證設計與套裝）。**空範圖＝空清單**（不是全部）。
 * 套裝的 `activation_policy` 決定表單上標「此方案可免審核發」還是「需我方審核」。
 */
export const listProducts = () => api<ProductSpec[]>(`${P}/products`)

// --- 我的客戶 -------------------------------------------------------------

export interface ResellerCustomer {
  id: string
  name: string
  domain: string | null
  notes: string | null
  created_at: string
  kind?: string | null
  profile_completed_at?: string | null
}
/**
 * 單一客戶的詳情（`GET /customers/{id}`）——清單投影 ＋ 一份 `profile`。
 *
 * 形狀與我方後台的 `CustomerDetail.profile` **同一個型別**（後端兩支端點共用
 * `api/_projections.py::customer_profile_out`），所以基本資料的共用表單元件
 * `components/CustomerProfileCard.vue` 兩個 realm 都吃得下。
 * 刻意**不含**邀請關係（`own_code`／`referred_by`／`referrals`）——那是我方的行銷歸因。
 */
export interface ResellerCustomerDetail extends ResellerCustomer {
  profile: CustomerProfile
}
export interface ResellerCustomerUser {
  id: string
  email: string
  active: boolean
  /** owner｜member（批93-C portal 成員模型）。 */
  role?: string | null
  /** invited＝已預建等對方首登；active＝已加入。 */
  status?: string | null
}

/**
 * 一頁清單（keyset 分頁，批93-D）。`next_cursor` 非 null＝還有下一頁，原封不動傳回去續頁。
 *
 * ★ 游標是**不透明字串**——別解析它（內容是 `(created_at, id)`，但那是後端的排序鍵，
 *   哪天換了排序，解析過它的呼叫端就全壞）。`ResellerLicensePage` 是同一個形狀，
 *   保留原名不動（既有呼叫端在用）。
 */
export interface ResellerPage<T> {
  items: T[]
  next_cursor: string | null
}

/** 分頁參數：`limit` 1–200（後端硬上限），預設 50。 */
export interface PageQuery {
  limit?: number
  cursor?: string | null
}

function pageQs(opts: PageQuery): string {
  const sp = new URLSearchParams()
  if (opts.limit) sp.set('limit', String(opts.limit))
  if (opts.cursor) sp.set('cursor', opts.cursor)
  const q = sp.toString()
  return q ? `?${q}` : ''
}

export const listCustomers = (opts: PageQuery = {}) =>
  api<ResellerPage<ResellerCustomer>>(`${P}/customers${pageQs(opts)}`)
export const getCustomer = (id: string) => api<ResellerCustomerDetail>(`${P}/customers/${id}`)
/** 新建客戶。**歸屬自動寫成自己**（body 不收 `reseller_id`）——沒有「認領既有客戶」這回事。 */
export const createCustomer = (body: { name: string; domain?: string | null }) =>
  api<ResellerCustomer>(`${P}/customers`, { method: 'POST', body })
/**
 * 修改客戶（含 `domain`）＋ **基本資料全欄**（批97 Bug 7，主人裁示 6：兩 realm 對稱）。
 *
 * body 與我方後台那支是**同一個型別**（`CustomerPatchBody`）：後端 `ResellerCustomerPatch`
 * 繼承同一份 `CustomerProfilePatch`，驗證規則與 422 形狀 `{code, field, message}` 逐字相同
 * ——所以共用表單元件不必分辨自己在哪個 realm。
 *
 * 網域撞到別家客戶 → 409，訊息**不含對方名稱**（避免拿這支端點當客戶名單探測器）。
 * 非自家客戶 → 404（同理，不回 403）。
 */
export const updateCustomer = (id: string, body: CustomerPatchBody) =>
  api<ResellerCustomerDetail>(`${P}/customers/${id}`, { method: 'PUT', body })
/**
 * 客戶基本資料表單的下拉選項——內容與我方後台那支**逐字相同**（同一個
 * `services/profile_options.py`）。
 *
 * ★ 為什麼要重複一支：本 realm 的規則是結構性的——「只有 `/api/admin/reseller/*` 是代理商
 *   打得到的，其餘一律 403」。讓代理商去打 `/api/admin/customer-profile-options` 就得在
 *   `require_admin` 上開一個例外，那正是這條規則想消滅的東西。
 */
export const getCustomerProfileOptions = () =>
  api<CustomerProfileOptions>(`${P}/customer-profile-options`)

export const listCustomerUsers = (id: string) =>
  api<ResellerCustomerUser[]>(`${P}/customers/${id}/users`)
/** 為自家客戶開 portal 帳號（預建；不寄信，請自行通知對方用該 Google 帳號登入）。 */
export const addCustomerUser = (id: string, email: string, role?: string) =>
  api<ResellerCustomerUser>(`${P}/customers/${id}/users`, {
    method: 'POST',
    body: role ? { email, role } : { email },
  })
export const removeCustomerUser = (id: string, userId: string) =>
  api<null>(`${P}/customers/${id}/users/${userId}`, { method: 'DELETE' })

// --- 授權（唯讀）----------------------------------------------------------

/**
 * 代理商看得到的授權。`LicenseBrief` 的欄位 ＋ 部署狀態（最後上線／版本／用量／到期倒數）。
 * **刻意不含** `blob`（要原文走 `licenseFile`，後端記稽核）與 `hostname`／`host_info`
 * （主機細節屬我方維運）。
 */
export interface ResellerLicense extends LicenseBrief {
  customer_id: string
  customer_name?: string | null
  instance_name?: string | null
  /** 部署最後一次心跳；null＝這張證從未被回報過（尚未安裝／離線授權）。 */
  last_seen?: string | null
  app_version?: string | null
  seats_in_use?: number | null
  partner_seats_in_use?: number | null
  quotas_in_use?: Record<string, number> | null
  /** 距到期天數（後端算好；null＝無到期日或尚未蓋章）。 */
  days_to_expiry?: number | null
  /**
   * 批108：這台是代管站（我方或代理商自建）→ 授權由中台派送，取證與換發端點一律 403。
   * **只有布林**，不回主機細節（哪幾個客戶同住一台 VM 是我方機密）。
   */
  hosted?: boolean
  /**
   * 批108：這台主機上生效中的加購子憑證（**不含 blob**）。
   * 同一台主機的多張主證會看到同一份——加購綁機器不綁證，這是對的。
   */
  addons?: ResellerAddon[]
}
/** 一頁授權（形狀＝`ResellerPage`；名字保留不動，既有呼叫端在用）。 */
export type ResellerLicensePage = ResellerPage<ResellerLicense>
/**
 * 自家客戶的授權清單。`bucket`（批121）＝狀態桶，**伺服器端**過濾
 * （`active`／`pending_activation`／`expiring_soon`／`expired`）。
 */
export const listLicenses = (opts: PageQuery & { bucket?: string } = {}) => {
  const q = pageQs(opts)
  const sep = q ? '&' : '?'
  const extra = opts.bucket ? `${sep}bucket=${encodeURIComponent(opts.bucket)}` : ''
  return api<ResellerLicensePage>(`${P}/licenses${q}${extra}`)
}

/** 四個狀態桶＋`all` 的**全表**筆數（批121；只含自家客戶的證）。 */
export interface ResellerLicenseCounts {
  all: number
  active: number
  pending_activation: number
  expiring_soon: number
  expired: number
}
export const licenseCounts = () => api<ResellerLicenseCounts>(`${P}/licenses/counts`)
/** 取授權原文交給客戶。**後端記稽核**（誰把哪張證帶走，事後一定會被問）。 */
export const licenseFile = (licenseId: string) =>
  api<{ license_id: string; blob: string; expires_at?: string | null }>(
    `${P}/licenses/${encodeURIComponent(licenseId)}/file`,
  )

/** 這台主機上的一張加購子憑證（**不含 blob**；清單直接掛在授權列上，不另外打端點）。 */
export interface ResellerAddon {
  addon_id: string
  label: string
  product: string
  instance_id: string
  status: string
  expires_at: string | null
  term_days: number | null
  term_minutes: number
  modules: Record<string, string>
  quotas: Record<string, unknown> | null
  seats: number | null
  max_product_lines: number | null
  trial: boolean
}

/**
 * 取加購子憑證原文（批108）。**後端記稽核**。
 *
 * 代理商 realm 原本完全沒有加購端點——離線客戶的加購子證只能由我方經手，代理商沒辦法
 * 把一個客戶服務到底。回應**刻意不含** `issued_by`／`notes`（我方員工 email 與內部備註
 * 不出我方 realm）。
 */
export const addonFile = (addonId: string) =>
  api<ResellerAddon & { blob: string }>(`${P}/addons/${encodeURIComponent(addonId)}/file`)

/**
 * 換發新證（批108）——掛的是與我方後台**逐位元相同**的那支本體。
 *
 * 權限不另設規則：內容偏離套裝模板一律 403 `activation_requires_approval`，
 * 代理商實際只換得了「試用套裝且逐字照模板」那一種，其餘請走授權申請流程。
 */
export const reissueLicense = (licenseId: string, input: Record<string, unknown>) =>
  api<{ license_id: string; superseded: string | null; base_status: string; policy: string }>(
    `${P}/licenses/${encodeURIComponent(licenseId)}/reissue`,
    { method: 'POST', body: input },
  )

// --- 換機申請（批108）-----------------------------------------------------

/** 自家客戶的換機單（含客戶自己在 portal 送的那些）。 */
export interface ResellerRebind {
  id: string
  customer_id: string | null
  customer_name: string | null
  old_license_id: string | null
  status: string
  /** 核准後的新證 id——取原文走 `licenseFile()`（**刻意沒有第三支取證端點**）。 */
  issued_license_id: string | null
  created_at: string
}

/**
 * 替自家客戶送換機申請。**核准仍然只有我方能做**（代理商白名單裡沒有 approve）。
 *
 * ★ 指紋一律由後端從 `new_request` 解析——這裡刻意沒有「直接給指紋」的參數，
 *   收了就等於讓代理商替客戶把證改綁到任意一台機器上。
 */
export const submitRebind = (body: {
  customer_id: string
  old_license_id: string
  new_request: Record<string, unknown>
}) => api<{ id: string; status: string }>(`${P}/rebind-requests`, { method: 'POST', body })

export const listRebinds = (opts: PageQuery = {}) =>
  api<ResellerPage<ResellerRebind>>(`${P}/rebind-requests${pageQs(opts)}`)

// --- 授權申請（draft → confirm）-------------------------------------------

/**
 * 建立授權草稿。四種 `kind`：
 * - `new`＝新證，要帶客戶主機跑出來的 `request_json`（含 instance_id 與指紋）
 * - `change`＝變更既有證的內容（指紋與產品沿用現行證，不收輸入）
 * - `renew`＝續約（帶 `base_license_id`＋期間）
 * - `addon`＝加購子憑證（帶 `base_license_id`＋模組／配額＋期間）
 */
export interface ResellerRequestInput {
  customer_id: string
  product: string
  kind: 'new' | 'change' | 'renew' | 'addon'
  /** `kind='new'` 必填；其餘不帶。 */
  request_json?: Record<string, unknown> | null
  /** `change`／`renew`／`addon` 必填：以哪一張現行證為基準。 */
  base_license_id?: string | null
  plan?: string | null
  seats?: number | null
  modules?: Record<string, string> | null
  quotas?: Record<string, number | null> | null
  max_product_lines?: number | null
  term_days?: number | null
  /** 批108：期限的分鐘餘數（0–1439）。總期限＝`term_days * 1440 + term_minutes`。 */
  term_minutes?: number | null
  note?: string | null
}
/**
 * 自家公司送的申請（含同事開的草稿）。批93-D 起是 **keyset 分頁**：回 `{items, next_cursor}`。
 *
 * 投影**不含我方員工的 email**（`reviewed_by`／`review_note`／`review.by`／`confirmed_by`）
 * ——那是後端的界線，不是前端藏起來。
 */
export const listRequests = (opts: PageQuery & { status?: string; onlyReturned?: boolean } = {}) => {
  const extra = new URLSearchParams()
  if (opts.status) extra.set('status', opts.status)
  if (opts.onlyReturned) extra.set('only_returned', 'true')
  const tail = extra.toString()
  const base = pageQs(opts)
  return api<ResellerPage<LicenseReq>>(
    `${P}/license-requests${base}${tail ? (base ? '&' : '?') + tail : ''}`,
  )
}

/**
 * 申請狀態桶的**全表**筆數（批121 §9）。
 *
 * ★ 桶與我方後台不同：rejected 與 cancelled 併成 `closed`（代理商只關心「這筆沒成」）。
 * ★ **不含換機申請**：那住另一張表，頁籤上的數字由 `listRebinds()` 自己出。
 */
export interface ResellerRequestCounts {
  all: number
  draft: number
  pending: number
  approved: number
  closed: number
  returned: number
}
export const myRequestCounts = () =>
  api<ResellerRequestCounts>(`${P}/license-requests/counts`)
export const createRequest = (body: ResellerRequestInput) =>
  api<LicenseReq>(`${P}/license-requests`, { method: 'POST', body })
/** 改草稿（只有自家公司的草稿改得動；非 draft → 409）。 */
export const updateRequest = (
  id: string,
  body: {
    plan?: string | null
    seats?: number | null
    modules?: Record<string, string> | null
    quotas?: Record<string, number | null> | null
    term_days?: number | null
    /** 批108：期限的分鐘餘數（0–1439）。總期限＝`term_days * 1440 + term_minutes`。 */
    term_minutes?: number | null
    max_product_lines?: number | null
    note?: string | null
  },
) => api<LicenseReq>(`${P}/license-requests/${id}`, { method: 'PUT', body })
/**
 * 確認送出。政策允許時**當場簽發**（回 `status:'approved'`＋`issued_license_id`），
 * 否則進 `pending` 等我方開通。內容偏離套裝模板 → 政策強制升為 superadmin
 * （`policy_reason='custom_content'`）。
 */
export const confirmRequest = (id: string) =>
  api<{
    status: string
    issued_license_id?: string | null
    policy: string
    policy_reason?: string | null
  }>(`${P}/license-requests/${id}/confirm`, { method: 'POST' })
/** 撤回（draft／pending 皆可）。 */
export const cancelRequest = (id: string) =>
  api<{ status: string }>(`${P}/license-requests/${id}/cancel`, { method: 'POST' })

// --- 兌換碼（批104）------------------------------------------------------
//
// 代理商在自家活動上能做的只有**發放**與**作廢**（建立與修改是 superadmin 的事，後端
// 這個 realm 根本沒有那兩支端點）。看得到的活動一律是 `reseller_id` 等於自己的那些——
// 我方自營與別家的活動打進來是 **404**（不是 403，不給當成「這個 uuid 存不存在」的探測器）。
//
// 兩處投影與我方 realm 不同（後端決定，不是前端藏）：
//   ① 活動**沒有** `notes`（內部備註不外流）
//   ② 兌換紀錄**沒有** `client_ip`（兌換人的 IP 屬我方稽核資料）

export const listRedeemCampaigns = (opts: PageQuery & { status?: string } = {}) => {
  const sp = new URLSearchParams()
  if (opts.limit) sp.set('limit', String(opts.limit))
  if (opts.cursor) sp.set('cursor', opts.cursor)
  if (opts.status) sp.set('status', opts.status)
  const q = sp.toString()
  return api<ResellerPage<RedeemCampaign>>(`${P}/redeem-campaigns${q ? `?${q}` : ''}`)
}

export const getRedeemCampaign = (id: string) => api<RedeemCampaign>(`${P}/redeem-campaigns/${id}`)

export const listRedeemCodes = (
  id: string,
  opts: PageQuery & { status?: string } = {},
) => {
  const sp = new URLSearchParams()
  if (opts.limit) sp.set('limit', String(opts.limit))
  if (opts.cursor) sp.set('cursor', opts.cursor)
  if (opts.status) sp.set('status', opts.status)
  const q = sp.toString()
  return api<RedeemCodePage>(`${P}/redeem-campaigns/${id}/codes${q ? `?${q}` : ''}`)
}

/** 發放自家活動的子碼（`issued_realm='reseller'`）。`to_customer_id` 只能填自家客戶。 */
export const issueRedeemCodes = (
  id: string,
  body: { count: number; to_customer_id?: string | null; note?: string | null },
) =>
  api<{ items: RedeemCode[]; campaign: RedeemCampaign }>(`${P}/redeem-campaigns/${id}/codes`, {
    method: 'POST',
    body,
  })

/** 作廢自家活動裡一組尚未兌換的子碼（含我方代發的那些）。 */
export const voidRedeemCode = (id: string, codeId: string) =>
  api<{ code: RedeemCode; campaign: RedeemCampaign }>(`${P}/redeem-campaigns/${id}/codes/${codeId}/void`, {
    method: 'POST',
  })

export const listRedemptions = (id: string, opts: PageQuery = {}) =>
  api<RedemptionPage>(`${P}/redeem-campaigns/${id}/redemptions${pageQs(opts)}`)
